import React from 'react';
import { Box, Typography, Container, Button } from '@mui/material';
import { styled } from '@mui/material/styles';

const BottomNavigationContainer = styled(Box)(({ theme, fixed }) => ({
  position: fixed ? 'fixed' : 'relative',
  bottom: fixed ? 0 : 'auto',
  left: 0,
  right: 0,
  width: '100%',
  backgroundColor: '#fff',
  borderTop: fixed ? '1px solid #E0E0E0' : 'none',
  borderBottom: fixed ? 'none' : '1px solid #E0E0E0',
  zIndex: fixed ? 1100 : 10,  
  padding: '16px 0',
  marginTop: fixed ? 0 : '16px',
  marginBottom: fixed ? 0 : '16px',
  boxShadow: fixed ? '0px -2px 10px rgba(0, 0, 0, 0.08)' : '0px 2px 4px rgba(0, 0, 0, 0.05)',
  backdropFilter: fixed ? 'blur(10px)' : 'none',
  borderRadius: fixed ? 0 : '12px',
}));

const PartNavItem = styled(Box)(({ theme, active }) => ({
  flex: 1,
  textAlign: 'center',
  padding: '10px',
  minHeight: '44px',
  boxSizing: 'border-box',
  backgroundColor: active ? '#F8F2FF' : '#fff',
  borderRadius: '10px',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  margin: 0,
  border: '1px solid #E0E0E0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: active ? '0 2px 8px rgba(153, 108, 254, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '5px',
    minHeight: '40px',
  },
}));

const ActivePartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  height: '100%',
  padding: 0,
  minHeight: 0,
}));

const QuestionIndicatorsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  overflowX: 'auto',
  justifyContent: 'flex-start',
  padding: 0,
  '&::-webkit-scrollbar': {
    height: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c0c0c0',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a0a0a0',
  },
}));

const QuestionNumberIndicator = styled(Box)(({ theme, active, answered, skipped, incorrect, isRange }) => ({
  width: isRange ? '30px' : '28px',
  height: isRange ? '30px' : '28px',
  borderRadius: '50%',
  display: 'inline-flex',
  flexDirection: isRange ? 'column' : 'row',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 4px',
  padding: 0,
  // Priority: incorrect > skipped > answered > active > default
  backgroundColor: active ? '#996CFE' : incorrect ? '#E53935' : skipped ? '#FF5252' : answered ? '#E8DFFF' : '#F0F0F0',
  color: active ? 'white' : incorrect ? 'white' : skipped ? 'white' : answered ? '#996CFE' : '#666',
  fontSize: '14px',
  fontWeight: isRange ? 400 : 500,
  cursor: 'pointer',
  lineHeight: 1,
  '&:hover': {
    backgroundColor: active ? '#7C4DFF' : incorrect ? '#C62828' : skipped ? '#E53935' : answered ? '#D6C6FF' : '#E0E0E0',
  },
  [theme.breakpoints.down('sm')]: {
    width: isRange ? '28px' : '26px',
    height: isRange ? '28px' : '26px',
    fontSize: '12px',
    margin: '0 2px',
  },
}));

const RangeLine = styled('div')(({ theme }) => ({
  width: '12px',
  height: '1.5px',
  backgroundColor: 'currentColor',
  margin: '1px 0',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: '10px',
    height: '1.5px',
    margin: '1px 0',
  },
}));

/**
 * Helper function to expand question range string to array
 */
function expandQuestionRange(range) {
  if (!range) return [];
  if (/^\d+$/.test(range.trim())) {
    return [range.trim()];
  }
  const rangeMatch = range.trim().match(/^(\d+)-(\d+)$/);
  if (rangeMatch) {
    const start = parseInt(rangeMatch[1]);
    const end = parseInt(rangeMatch[2]);
    if (start <= end) {
      const numbers = [];
      for (let i = start; i <= end; i++) {
        numbers.push(i.toString());
      }
      return numbers;
    }
  }
  return [];
}

/**
 * Helper function to group question numbers that belong to the same MCQ range
 * @param {string[]} questionNumbers - Array of question numbers
 * @param {string} passageText - Section passage text to check for MCQ ranges
 * @returns {Array} Array of objects: { type: 'single' | 'range', value: string, numbers: string[] }
 */
function groupQuestionNumbersForMCQ(questionNumbers, passageText) {
  if (!passageText || !questionNumbers.length) {
    return questionNumbers.map(qNum => ({ type: 'single', value: qNum, numbers: [qNum] }));
  }
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(passageText, 'text/html');
  
  // Find all MCQ ranges
  const mcqRanges = new Map(); // Map: "17" -> "17-18", "18" -> "17-18"
  doc.querySelectorAll('.mcq-container[data-mcq-type="multiple"]').forEach(mcqContainer => {
    const questionRange = mcqContainer.getAttribute('data-question-range') || '';
    if (questionRange) {
      const expandedNums = expandQuestionRange(questionRange);
      expandedNums.forEach(qNum => {
        mcqRanges.set(qNum, questionRange);
      });
    }
  });
  
  // Group question numbers
  const grouped = [];
  const processed = new Set();
  
  questionNumbers.forEach(qNum => {
    if (processed.has(qNum)) return;
    
    const range = mcqRanges.get(qNum);
    if (range) {
      // This is part of a range - find all numbers in this range
      const rangeNums = expandQuestionRange(range);
      const numsInQuestionList = rangeNums.filter(n => questionNumbers.includes(n));
      
      if (numsInQuestionList.length > 1) {
        // Group as range
        grouped.push({
          type: 'range',
          value: `${numsInQuestionList[0]}–${numsInQuestionList[numsInQuestionList.length - 1]}`,
          numbers: numsInQuestionList,
          range: range
        });
        numsInQuestionList.forEach(n => processed.add(n));
      } else {
        // Only one number from range is in question list - treat as single
        grouped.push({ type: 'single', value: qNum, numbers: [qNum] });
        processed.add(qNum);
      }
    } else {
      // Single question
      grouped.push({ type: 'single', value: qNum, numbers: [qNum] });
      processed.add(qNum);
    }
  });
  
  return grouped;
}

/**
 * Reusable IELTS Bottom Navigation Component
 * 
 * @param {Object} props
 * @param {Array} props.parts - Array of part/section objects or numbers
 * @param {number} props.currentPart - Currently active part index or number
 * @param {Function} props.onPartChange - Callback when part is clicked
 * @param {boolean} props.disabled - Whether navigation is disabled
 * @param {Function} props.getQuestionNumbers - Function to get question numbers for a part (optional)
 * @param {Object} props.allAnswers - Object with answers keyed by question number (optional)
 * @param {Function} props.isQuestionAnswered - Function to check if a question is answered: (sectionIdx, questionNumber) => boolean (optional, takes precedence over allAnswers)
 * @param {Function} props.isQuestionIncorrect - Function to check if a question is incorrect: (sectionIdx, questionNumber) => boolean (optional, for results mode)
 * @param {Array} props.skippedQuestions - Array of skipped question numbers (optional)
 * @param {Function} props.onQuestionClick - Callback when question number is clicked: (questionNumber, sectionIdx?) => void (optional)
 * @param {Function} props.getPartName - Function to get part name (optional)
 * @param {string} props.mode - 'detailed' (with question indicators), 'task' (with task labels), or 'simple' (just buttons)
 * @param {Array} props.sections - Array of sections for simple/task mode (optional)
 * @param {number} props.currentSection - Current section index for simple/task mode (optional)
 * @param {Object} props.taskLabels - Object mapping section indices to task labels (optional, for task mode)
 */
const IELTSBottomNavigation = ({
  parts = [],
  currentPart,
  onPartChange,
  disabled = false,
  getQuestionNumbers,
  allAnswers = {},
  isQuestionAnswered,
  isQuestionIncorrect,
  skippedQuestions = [],
  onQuestionClick,
  getPartName,
  mode = 'detailed',
  sections = [],
  currentSection = 0,
  taskLabels = {},
  fixed = true,
  visitedParts = [],
  lockUnvisitedParts = false,
}) => {
  // Task mode for WritingTestUI - same structure as detailed but shows task labels instead of question circles
  // Text always stays centered (unlike detailed mode where it moves left for question circles)
  if (mode === 'task') {
    return (
      <BottomNavigationContainer fixed={fixed}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: 1 }}>
            {sections.map((section, idx) => {
              const isCurrent = currentSection === idx;
              const taskLabel = taskLabels[idx] || `Task ${idx + 1}`;
              
              return (
                <PartNavItem 
                  key={section.id || idx}
                  active={isCurrent}
                  onClick={() => !disabled && onPartChange && onPartChange(idx)}
                  sx={disabled ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    width: '100%',
                    height: '100%',
                    minHeight: 0
                  }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: isCurrent ? 600 : 500, 
                        color: isCurrent ? '#996CFE' : '#333',
                        fontSize: { xs: '13px', sm: '14px', md: '15px' },
                        textAlign: 'center',
                        letterSpacing: '0.3px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {taskLabel}
                    </Typography>
                  </Box>
                </PartNavItem>
              );
            })}
          </Box>
        </Container>
      </BottomNavigationContainer>
    );
  }

  // Simple mode for WritingTestUI style navigation
  if (mode === 'simple') {
    return (
      <BottomNavigationContainer fixed={fixed}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            {sections.map((section, idx) => (
              <Button
                key={section.id || idx}
                variant={currentSection === idx ? 'contained' : 'outlined'}
                sx={{
                  backgroundColor: currentSection === idx ? '#996CFE' : '#fff',
                  color: currentSection === idx ? '#fff' : '#996CFE',
                  borderColor: '#996CFE',
                  '&:hover': {
                    backgroundColor: currentSection === idx ? '#7C4DFF' : '#F8F2FF',
                    borderColor: '#996CFE',
                  },
                  minWidth: '120px',
                }}
                onClick={() => !disabled && onPartChange && onPartChange(idx)}
                disabled={disabled}
              >
                {taskLabels[idx] || `Task ${idx + 1}`}
              </Button>
            ))}
          </Box>
        </Container>
      </BottomNavigationContainer>
    );
  }

  // Detailed mode for ReadingTestUI and ListeningTestUI style navigation
  return (
    <BottomNavigationContainer fixed={fixed}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: 1 }}>
          {parts.map((part, idx) => {
            const partNumber = typeof part === 'number' ? part : (part.part || idx + 1);
            const isCurrent = currentPart === partNumber;
            const sectionIdx = part.sectionIdx !== undefined ? part.sectionIdx : idx;
            
            // Check if this part is locked
            const isVisited = visitedParts.includes(partNumber);
            const isLocked = lockUnvisitedParts && !isVisited && !isCurrent;
            
            // Get part name
            const partName = getPartName 
              ? getPartName(part, idx)
              : (part.part_name || `Part ${partNumber}`);
            
            // Get question numbers for this part
            const questionNumbers = getQuestionNumbers 
              ? getQuestionNumbers(sectionIdx)
              : [];
            
            // Group question numbers for MCQ ranges
            // Check both passage_text and contentHtml for MCQ containers
            const passageText = sections[sectionIdx]?.passage_text || sections[sectionIdx]?.contentHtml || sections[sectionIdx]?.content_html || '';
            const groupedQuestions = groupQuestionNumbersForMCQ(questionNumbers, passageText);

            return (
              <PartNavItem 
                key={partNumber}
                active={isCurrent}
                onClick={() => !disabled && !isLocked && onPartChange && onPartChange(partNumber)}
                sx={(disabled || isLocked) ? { 
                  opacity: isLocked ? 0.4 : 0.5, 
                  cursor: 'not-allowed', 
                  pointerEvents: 'none',
                  backgroundColor: isLocked ? '#f5f5f5' : undefined,
                  position: 'relative',
                  '&::after': isLocked ? {
                    content: '"🔒"',
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    fontSize: '12px',
                  } : {}
                } : {}}
              >
                {isCurrent ? (
                  <ActivePartHeader>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#996CFE',
                        fontSize: { xs: '13px', sm: '14px', md: '15px' },
                        mr: 1.5,
                        whiteSpace: 'nowrap',
                        letterSpacing: '0.3px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {partName}
                    </Typography>
                    {groupedQuestions.length > 0 && (
                      <QuestionIndicatorsContainer sx={{ flex: 1 }}>
                        {groupedQuestions.map((group, groupIdx) => {
                          // For range, check if any question in range is answered/incorrect/skipped
                          const checkNumbers = group.numbers;
                          const isAnswered = isQuestionAnswered
                            ? checkNumbers.some(qNum => isQuestionAnswered(sectionIdx, qNum))
                            : checkNumbers.some(qNum => 
                                allAnswers[qNum] !== undefined && 
                                allAnswers[qNum] !== '' && 
                                allAnswers[qNum] !== null
                              );
                          const isIncorrect = isQuestionIncorrect
                            ? checkNumbers.some(qNum => isQuestionIncorrect(sectionIdx, qNum))
                            : false;
                          const isSkipped = checkNumbers.some(qNum => skippedQuestions.includes(qNum));
                          
                          return (
                            <QuestionNumberIndicator
                              key={group.type === 'range' ? `range-${group.range}-${groupIdx}` : `single-${group.value}-${groupIdx}`}
                              active={false}
                              answered={isAnswered && !isIncorrect}
                              skipped={isSkipped && !isIncorrect}
                              incorrect={isIncorrect}
                              isRange={group.type === 'range'}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!disabled && onQuestionClick) {
                                  // For range, click the first question number
                                  onQuestionClick(checkNumbers[0], sectionIdx);
                                }
                              }}
                            >
                              {group.type === 'range' ? (
                                <>
                                  <span>{checkNumbers[0]}</span>
                                  <RangeLine />
                                  <span>{checkNumbers[checkNumbers.length - 1]}</span>
                                </>
                              ) : (
                                group.value
                              )}
                            </QuestionNumberIndicator>
                          );
                        })}
                      </QuestionIndicatorsContainer>
                    )}
                  </ActivePartHeader>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    width: '100%',
                    minHeight: 0
                  }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 500, 
                        color: '#666',
                        fontSize: { xs: '13px', sm: '14px', md: '15px' },
                        textAlign: 'center',
                        letterSpacing: '0.2px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {partName}: {questionNumbers.length} questions
                    </Typography>
                  </Box>
                )}
              </PartNavItem>
            );
          })}
        </Box>
      </Container>
    </BottomNavigationContainer>
  );
};

export default IELTSBottomNavigation;

